import { createContext, useContext, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { GrokVoiceSession, type VoiceStatus } from "@/lib/voiceRealtime";
import { useUserPref } from "@/hooks/useUserPref";
import { sendCrmSms } from "@/lib/crm-sms-send";
import { emitCrmDataChanged } from "@/lib/crmDataEvents";
import { toast } from "sonner";
import {
  getAuthHeaders,
  extractRecordCards,
  previewKey,
  toolActivityLabel,
  voiceTurnsToMessages,
  playVoiceCue,
  preloadVoiceCues,
  startThinkingChime,
  stopThinkingChime,
  setVoiceCueVolume,
  isVoiceCueActive,
  sameRecordScope,
  type VoiceTurn,
  type ChatMessage,
  type PendingPreview,
  type PendingActionState,
  type RecordScopeType,
} from "@/components/dashboard/ai-assistant/voiceShared";
import type { PlanStep, PlanStepResult } from "@/components/dashboard/ai-assistant/PlanCard";
import type { CampaignDraft, CampaignSendResult } from "@/components/dashboard/ai-assistant/CampaignDraftCard";

/**
 * CopilotVoiceContext — the SINGLE live Grok realtime voice call, hoisted out
 * of `DashboardAIAssistant` so it OUTLIVES any surface that presents it.
 *
 * A voice call started inside a record's Grok tab keeps running when the detail
 * sheet closes (roaming); the global FAB reattaches to it. The provider is
 * mounted ONCE at the dashboard root (never unmounts while on the dashboard),
 * so a surface unmount no longer tears down the WebSocket + audio. This owns
 * only VOICE — text chat stays entirely in `DashboardAIAssistant`.
 *
 * See RECORD_COPILOT_PLAN.md (Phase 2 · #1). One engine, never forked.
 */

export type CopilotRecordScope = { type: RecordScopeType; id: string; label?: string };

export interface StartVoiceOpts {
  /** Authoritative business the call acts on (surface's effectiveBusinessId). */
  businessId: string | null;
  /** Acting user (honors "Login As"). */
  actingUserId: string | null;
  /** The record this call is focused on (Grok tab), or null for the global copilot. */
  recordScope: CopilotRecordScope | null;
  /** The surface's current thread — the call APPENDS its transcript here (null = new thread). */
  threadId: string | null;
  /** Recent text turns so a mid-thread call continues, not cold-starts. */
  contextText: string | null;
  /** Brand-new conversation → Grok opens with a short spoken greeting. */
  greet: boolean;
  /** Snapshot of the initiating surface's messages — rendered above the live
   *  turns in the overlay so a roamed call reads consistently from ANY surface. */
  priorMessages: ChatMessage[];
}

export type StartVoiceResult =
  | { status: "started" }
  | { status: "already-active-same" }
  | { status: "already-active-different"; activeScope: CopilotRecordScope | null };

export interface FinalizedCall {
  threadId: string | null;
  recordScope: CopilotRecordScope | null;
  messages: ChatMessage[];
}

export interface CopilotVoiceValue {
  // live call state
  voiceActive: boolean;
  voiceStatus: VoiceStatus;
  voiceLevel: number;
  /** Grok's live playback level — drives the violet bars while he talks. */
  voiceOutLevel: number;
  voiceMuted: boolean;
  voiceError: string | null;
  voiceTurns: VoiceTurn[];
  voiceSuggestions: string[];
  voiceActivity: string | null;
  voiceSpeed: number;
  setVoiceSpeed: (n: number | ((p: number) => number)) => void;

  // captured call identity (for the presenting surface: banner, thread align, merge-back)
  callRecordScope: CopilotRecordScope | null;
  callThreadId: string | null;
  priorMessages: ChatMessage[];

  // the voice-only confirm queue (raised by voice tool calls)
  voicePendingPreviews: PendingPreview[];
  setVoicePendingPreviews: React.Dispatch<React.SetStateAction<PendingPreview[]>>;
  voicePendingAction: PendingActionState | null;
  setVoicePendingAction: (v: PendingActionState | null) => void;
  voiceUploadTarget: { leadId: string; projectId: string | null } | null;
  setVoiceUploadTarget: (v: { leadId: string; projectId: string | null } | null) => void;
  voiceConfirmBusy: boolean;
  voiceCrmSendBusy: boolean;

  // controls
  startVoice: (opts: StartVoiceOpts) => Promise<StartVoiceResult>;
  stopVoice: () => void;
  toggleMute: () => void;
  sendVoiceSuggestion: (text: string) => void;
  /** Dismiss the error banner without ending the call. */
  clearVoiceError: () => void;

  // voice confirm handlers (bound to the call identity + voice queue)
  confirmPending: (p: PendingPreview) => Promise<void>;
  confirmAllPending: () => Promise<void>;
  executePlan: (p: PendingPreview) => Promise<PlanStepResult[] | null>;
  sendCampaign: (p: PendingPreview, drafts: CampaignDraft[]) => Promise<CampaignSendResult | null>;
  sendSmsViaCrmNow: (opts: { leadId: string; businessId: string; message: string; name?: string | null; phone?: string | null; provider?: "dialpad" | "twilio" | null; fromNumberId?: string | null }) => Promise<boolean>;

  // merge-back handshake ("one conversation across voice+text")
  finalizedCall: FinalizedCall | null;
  clearFinalizedCall: () => void;
}

const CopilotVoiceContext = createContext<CopilotVoiceValue | null>(null);

// After a commit/send, Grok speaks ONE short line that keeps the conversation
// going (John 2026-07-09: "sent" then silence felt dead — resume the open
// item or ask what's next). Scoped to that single response via requestFollowUp.
const FOLLOW_UP_LINE =
  "Speak exactly ONE short, natural sentence: quickly confirm it's done, then keep the conversation moving — pick up the next open thing we were already working on, or ask what's next if nothing's open. Do not list or re-read details of what was just committed.";

// Tool names whose successful commit changes what dashboards display.
const WRITE_TOOL = /^(create|update|set|archive|convert|move|complete)_/;

export function CopilotVoiceProvider({ children }: { children: ReactNode }) {
  // ── live call state ──
  const [voiceActive, setVoiceActive] = useState(false);
  const [voiceStatus, setVoiceStatus] = useState<VoiceStatus>("idle");
  const [voiceLevel, setVoiceLevel] = useState(0);
  const [voiceOutLevel, setVoiceOutLevel] = useState(0);
  const [voiceMuted, setVoiceMuted] = useState(false);
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [voiceTurns, setVoiceTurns] = useState<VoiceTurn[]>([]);
  const [voiceSuggestions, setVoiceSuggestions] = useState<string[]>([]);
  const [voiceActivity, setVoiceActivity] = useState<string | null>(null);
  const [voiceSpeed, setVoiceSpeed] = useUserPref<number>("ai_voice_speed", 1);
  // Voice sound/volume prefs (a slim slice of ai_chat_prefs) — read-only
  // subscription so this eager provider doesn't import the lazy settings
  // dialog. chime gates ALL THREE sounds; the volumes + echo guard apply
  // LIVE to the running call.
  const [voiceSoundPrefs] = useUserPref<{
    chime?: boolean;
    chimeVolume?: number;
    voiceVolume?: number;
    echoGuard?: "off" | "balanced" | "strong";
  }>("ai_chat_prefs", { chime: true });
  const voiceSoundsOn = voiceSoundPrefs.chime !== false;
  const voiceSoundsRef = useRef(voiceSoundsOn);
  useEffect(() => { voiceSoundsRef.current = voiceSoundsOn; }, [voiceSoundsOn]);
  const chimeVolume = voiceSoundPrefs.chimeVolume ?? 65;
  const voiceVolume = voiceSoundPrefs.voiceVolume ?? 100;
  const echoGuard = voiceSoundPrefs.echoGuard ?? "balanced";
  const voiceVolumeRef = useRef(voiceVolume);
  useEffect(() => { voiceVolumeRef.current = voiceVolume; }, [voiceVolume]);
  const echoGuardRef = useRef(echoGuard);
  useEffect(() => { echoGuardRef.current = echoGuard; }, [echoGuard]);
  // Cue gain lives in the shared sound module; keep it fed.
  useEffect(() => { setVoiceCueVolume(chimeVolume / 100); }, [chimeVolume]);

  // ── captured identity (state for render, refs for the session callbacks) ──
  const [callRecordScope, setCallRecordScope] = useState<CopilotRecordScope | null>(null);
  const [callThreadId, setCallThreadId] = useState<string | null>(null);
  const [priorMessages, setPriorMessages] = useState<ChatMessage[]>([]);

  // ── voice-only confirm queue ──
  const [voicePendingPreviews, setVoicePendingPreviews] = useState<PendingPreview[]>([]);
  const [voicePendingAction, setVoicePendingAction] = useState<PendingActionState | null>(null);
  const [voiceUploadTarget, setVoiceUploadTarget] = useState<{ leadId: string; projectId: string | null } | null>(null);
  const [voiceConfirmBusy, setVoiceConfirmBusy] = useState(false);
  const [voiceCrmSendBusy, setVoiceCrmSendBusy] = useState(false);

  // ── merge-back handshake ──
  const [finalizedCall, setFinalizedCall] = useState<FinalizedCall | null>(null);
  const clearFinalizedCall = useCallback(() => setFinalizedCall(null), []);

  // ── refs (read synchronously by the long-lived session callbacks) ──
  const voiceSessionRef = useRef<GrokVoiceSession | null>(null);
  const voiceActiveRef = useRef(false);
  const voiceSpeedRef = useRef(voiceSpeed);
  useEffect(() => { voiceSpeedRef.current = voiceSpeed; }, [voiceSpeed]);
  const voiceTurnsRef = useRef<VoiceTurn[]>([]);
  useEffect(() => { voiceTurnsRef.current = voiceTurns; }, [voiceTurns]);
  // Pending-preview mirror + executePlan handle for execVoiceTool (its closure
  // is frozen for the life of the call): the verbal-confirm intercept needs
  // the CURRENT staged plan card and the executePlan defined further down.
  const voicePendingPreviewsRef = useRef<PendingPreview[]>([]);
  useEffect(() => { voicePendingPreviewsRef.current = voicePendingPreviews; }, [voicePendingPreviews]);
  const executePlanRef = useRef<((p: PendingPreview, opts?: { silent?: boolean }) => Promise<PlanStepResult[] | null>) | null>(null);
  const assistantNewTurnRef = useRef(false);
  const lastUserTextRef = useRef("");
  const lastAssistantTextRef = useRef("");
  const voiceSavedCountRef = useRef(0);
  const cachedHeadersRef = useRef<Record<string, string> | null>(null);
  // True while voiceError came from a live realtime error event (recoverable);
  // such banners auto-clear once Grok responds normally. Fatal ones stick.
  const transientErrorRef = useRef(false);
  const syncChainRef = useRef<Promise<void>>(Promise.resolve());
  // Call identity — the immutable-for-this-call context the session acts in.
  const callBusinessIdRef = useRef<string | null>(null);
  const callActingUserIdRef = useRef<string | null>(null);
  const callRecordScopeRef = useRef<CopilotRecordScope | null>(null);
  const callThreadIdRef = useRef<string | null>(null);
  useEffect(() => { callThreadIdRef.current = callThreadId; }, [callThreadId]);

  // Append/replace the in-progress transcript turn for a role. We coalesce
  // consecutive deltas of the same role into one growing bubble.
  const appendVoiceTurn = useCallback((role: "user" | "assistant", text: string, replace: boolean) => {
    if (!text) return;
    const forceNew = role === "assistant" && assistantNewTurnRef.current;
    if (forceNew) assistantNewTurnRef.current = false;
    setVoiceTurns((prev) => {
      const copy = [...prev];
      const last = copy[copy.length - 1];
      if (last && last.role === role && !forceNew) {
        copy[copy.length - 1] = { role, text: replace ? text : last.text + text };
      } else {
        copy.push({ role, text });
      }
      return copy;
    });
  }, []);

  // Actually SEND a text from the business's CRM number, right now (spoken "send
  // it from the CRM" or the card's "Send via CRM" button). Uses the same
  // authenticated sms-api path the CRM text thread uses. `viaTool` marks the
  // model-driven path (mid function-call): there the turn's own single
  // continuation speaks the follow-up; the tapped path requests one directly.
  const sendSmsViaCrmNow = useCallback(async (opts: { leadId: string; businessId: string; message: string; name?: string | null; phone?: string | null; provider?: "dialpad" | "twilio" | null; fromNumberId?: string | null; viaTool?: boolean }): Promise<boolean> => {
    const message = (opts.message || "").trim();
    if (!opts.leadId || !opts.businessId || !message) {
      toast.error("Nothing to send — missing a lead or message text.");
      return false;
    }
    setVoiceCrmSendBusy(true);
    voiceSessionRef.current?.interrupt();
    // Pre-exec note (see commitPreview): the send is two server round-trips —
    // if the user talks over it, Grok must already know it's in flight.
    const smsPreNote = voiceSessionRef.current?.notifyContext(
      `[I told the system to send that text from the CRM number — it is sending RIGHT NOW. Treat it as sent; do NOT call send_sms again for it.]`,
    );
    try {
      // Executes the server-chosen line (Dialpad or a specific Twilio number).
      await sendCrmSms({ leadId: opts.leadId, businessId: opts.businessId, message, phone: opts.phone, provider: opts.provider, fromNumberId: opts.fromNumberId });

      const doneText = `Sent ✓ — text to ${opts.name || "them"}`;
      setVoicePendingAction(null);
      setVoiceTurns((prev) => [...prev, { role: "assistant", text: doneText }]);
      voiceSessionRef.current?.replaceTapNote(
        smsPreNote,
        `[The text was just SENT from the CRM number successfully. Do NOT call send_sms again for it. Briefly confirm it went out and CONTINUE the conversation — the next open item we were discussing, or what's next.]`,
      );
      // Keep talking after the send: the model path gets its turn's own single
      // continuation back (interrupt() had suppressed it); a tapped Send asks
      // for one scoped follow-up line instead of dropping to silence.
      if (opts.viaTool) voiceSessionRef.current?.allowContinue();
      else voiceSessionRef.current?.requestFollowUp(FOLLOW_UP_LINE);
      emitCrmDataChanged();
      return true;
    } catch (e) {
      voiceSessionRef.current?.replaceTapNote(smsPreNote, `[CORRECTION: that CRM text FAILED to send — it did NOT go out.]`);
      toast.error(e instanceof Error ? e.message : "Couldn't send the text — try again.");
      return false;
    } finally {
      setVoiceCrmSendBusy(false);
    }
  }, []);

  // Execute a tool the voice model called: run it server-side via tool-exec,
  // surface confirm cards / client actions, and return the result so the model
  // can continue. Reads identity from refs (immutable for the live call).
  const execVoiceTool = useCallback(async (name: string, args: Record<string, unknown>): Promise<unknown> => {
    setVoiceActivity(toolActivityLabel(name));
    try {
      // Verbal plan confirm: the model re-called propose_plan with
      // confirmed:true after the user SAID yes. Run the STAGED card (zero
      // drift from what's on screen) through the exact same path as a tap —
      // the card animates, honest notes flow, and the model gets real
      // per-step results back. 2026-07-10: without this, spoken "Confirm all"
      // left the model claiming a plan was running that never started.
      if (name === "propose_plan" && args?.confirmed === true) {
        const staged = voicePendingPreviewsRef.current.find((x) => x.name === "propose_plan" && x.plan?.length);
        if (staged && executePlanRef.current) {
          const results = await executePlanRef.current(staged, { silent: true });
          if (!results) return { error: "The plan failed to start — nothing was committed. Tell the user and offer to try again." };
          const total = staged.plan?.length ?? results.length;
          const completed = results.filter((r) => r.ok).length;
          const failed = results.find((r) => !r.ok) || null;
          return {
            success: completed === total,
            action: "plan_executed",
            title: staged.title,
            completed,
            total,
            results: results.map((r) => ({ index: r.index, title: r.title, tool: r.tool, ok: r.ok, ...(r.error ? { error: r.error } : {}) })),
            message: completed === total
              ? `All ${total} steps committed. Tell the user it's done in ONE short line.`
              : `Steps 1-${completed} committed, then step ${failed?.index} ("${failed?.title}") FAILED: ${failed?.error} Nothing after it ran. Tell the user plainly which step failed and why, and offer to finish that part another way. Do NOT claim success and do NOT say it's still running.`,
          };
        }
        // No staged card (dismissed or never shown) — never write 2-8 records
        // on a bare confirmed:true; downgrade to a fresh preview instead.
        args = { ...args };
        delete (args as Record<string, unknown>).confirmed;
      }
      const headers = await getAuthHeaders();
      const resp = await fetch("/api/ai/tool-exec", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name, args, business_id: callBusinessIdRef.current, acting_user_id: callActingUserIdRef.current }),
      });
      const data = await resp.json().catch(() => ({}));
      const result = (data.result ?? data) as Record<string, unknown>;
      if (result?.requires_confirmation) {
        const key = previewKey(name, args);
        setVoicePendingPreviews((prev) =>
          prev.some((x) => x.key === key)
            ? prev
            : [
                ...prev,
                {
                  id: crypto.randomUUID(),
                  key,
                  name,
                  action: String(result.action ?? name),
                  title: String(result.title ?? "Confirm action"),
                  fields: (result.fields as Record<string, unknown>) || {},
                  args,
                  ...(Array.isArray(result.plan) ? { plan: result.plan as PlanStep[] } : {}),
                  ...(Array.isArray(result.drafts) ? { drafts: result.drafts as CampaignDraft[] } : {}),
                },
              ],
        );
        // Persist the staged-write marker onto the thread (text mode does this
        // server-side). Without it, a dropped call forgets what was staged and
        // the next session re-interrogates the user for details it already had
        // (2026-07-10: zip + appointment time asked twice).
        if (callThreadIdRef.current) {
          const { confirmed: _c, ...previewArgs } = args as Record<string, unknown>;
          void fetch("/api/ai/save-voice-thread", {
            method: "POST",
            headers: { ...headers, "Content-Type": "application/json" },
            credentials: "include",
            body: JSON.stringify({
              business_id: callBusinessIdRef.current,
              thread_id: callThreadIdRef.current,
              messages: [{ role: "assistant", content: "", preview: { name, args: previewArgs }, timestamp: new Date().toISOString() }],
              record_type: callRecordScopeRef.current?.type ?? undefined,
              record_id: callRecordScopeRef.current?.id ?? undefined,
            }),
          }).catch(() => { /* marker is best-effort */ });
        }
      } else if (result?.client_action) {
        const ca = String(result.client_action);
        if (ca === "attach_files" && typeof result.lead_id === "string") {
          setVoiceUploadTarget({ leadId: result.lead_id as string, projectId: (result.project_id as string) || null });
        } else if (ca === "send_sms_crm") {
          const ok = await sendSmsViaCrmNow({
            leadId: String(result.lead_id ?? ""),
            businessId: callBusinessIdRef.current ?? "",
            message: String(result.message ?? ""),
            name: (result.name as string) ?? null,
            phone: (result.phone as string) ?? null,
            provider: (result.send_provider as "dialpad" | "twilio") ?? null,
            fromNumberId: (result.from_number_id as string) ?? null,
            viaTool: true,
          });
          return ok
            ? { success: true, sent: true, note: "Text sent from the CRM number." }
            : { error: "Couldn't send the text from the CRM number." };
        } else if (ca === "send_sms" || ca === "initiate_call") {
          setVoicePendingAction({
            client_action: ca,
            lead_id: result.lead_id as string,
            phone: result.phone as string,
            name: result.name as string,
            message: result.message as string,
            send_from_options: (result.send_from_options as PendingActionState["send_from_options"]) ?? undefined,
          });
        }
      }
      // A write COMMITTED (not a preview) → dashboards the user is watching
      // (Home tiles, lists) refetch immediately.
      if (!result?.error && !result?.requires_confirmation && WRITE_TOOL.test(name)) emitCrmDataChanged();
      // Verbal confirm: when the model re-calls the tool with confirmed:true
      // after a spoken "yes" and it commits, dismiss that write's card.
      if (args?.confirmed === true && !result?.requires_confirmation && !result?.error) {
        const key = previewKey(name, args);
        setVoicePendingPreviews((prev) => {
          if (prev.some((x) => x.key === key)) return prev.filter((x) => x.key !== key);
          const sameName = prev.filter((x) => x.name === name);
          return sameName.length === 1 ? prev.filter((x) => x.name !== name) : prev;
        });
      }
      const cards = extractRecordCards(name, result);
      if (cards.length > 0) {
        setVoiceTurns((prev) => [...prev, { role: "records", cards }]);
      }
      return result;
    } catch (e) {
      return { error: e instanceof Error ? e.message : "tool failed" };
    } finally {
      setVoiceActivity(null);
    }
  }, [sendSmsViaCrmNow]);

  // Tapping Confirm COMMITS deterministically: re-run the staged tool with the
  // original args + confirmed:true and tell Grok it's committed.
  const commitPreview = useCallback(async (p: PendingPreview): Promise<boolean> => {
    // Tell Grok at TAP TIME, before any network round-trip. John taps Confirm
    // and immediately asks "did that go through?" — server VAD auto-answers
    // before tool-exec returns, so a note sent only AFTER the POST loses the
    // race (2026-07-09 session: "Not yet—I'm waiting on your confirmation"
    // right after the card showed Done ✓). Upgraded to the definitive wording
    // (or a CORRECTION) below once the commit resolves.
    const preNote = voiceSessionRef.current?.notifyContext(
      `[I tapped the Confirm button for "${p.title}" — the system is committing it RIGHT NOW. Treat it as DONE; never say it's pending or waiting on me.]`,
    );
    const failNote = (detail: string) =>
      voiceSessionRef.current?.replaceTapNote(preNote, `[CORRECTION: the tap-commit for "${p.title}" FAILED (${detail}) — it did NOT happen; the card is still pending.]`);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch("/api/ai/tool-exec", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: p.name, args: { ...p.args, confirmed: true }, business_id: callBusinessIdRef.current, acting_user_id: callActingUserIdRef.current }),
      });
      const data = await resp.json().catch(() => ({}));
      const result = (data.result ?? data) as Record<string, unknown>;
      if (!resp.ok || result?.error) {
        failNote(String(result?.error || `HTTP ${resp.status}`));
        toast.error(String(result?.error || "Couldn't complete that — try again."));
        return false;
      }
      const doneText = `Done ✓ — ${p.title}`;
      const cards = extractRecordCards(p.name, result);
      setVoiceTurns((prev) => [
        ...prev,
        { role: "assistant", text: doneText },
        ...(cards.length ? [{ role: "records" as const, cards }] : []),
      ]);
      voiceSessionRef.current?.replaceTapNote(
        preNote,
        `["${p.title}" is committed successfully via my on-screen tap. Do NOT call any tool for it and do NOT redo it.]`,
      );
      emitCrmDataChanged();
      setVoicePendingPreviews((prev) => prev.filter((x) => x.id !== p.id));
      return true;
    } catch (e) {
      failNote("network error");
      toast.error(e instanceof Error ? e.message : "Couldn't complete that — try again.");
      return false;
    }
  }, []);

  const confirmPending = useCallback(async (p: PendingPreview) => {
    if (voiceConfirmBusy) return;
    voiceSessionRef.current?.interrupt();
    setVoiceConfirmBusy(true);
    try {
      const ok = await commitPreview(p);
      // One short spoken line to pick the conversation back up — never
      // silence after a tap.
      if (ok) voiceSessionRef.current?.requestFollowUp(FOLLOW_UP_LINE);
    } finally {
      setVoiceConfirmBusy(false);
    }
  }, [voiceConfirmBusy, commitPreview]);

  const confirmAllPending = useCallback(async () => {
    if (voiceConfirmBusy) return;
    voiceSessionRef.current?.interrupt();
    setVoiceConfirmBusy(true);
    try {
      let anyOk = false;
      for (const p of voicePendingPreviews) {
        anyOk = (await commitPreview(p)) || anyOk;
      }
      // ONE follow-up for the whole batch, not one per card.
      if (anyOk) voiceSessionRef.current?.requestFollowUp(FOLLOW_UP_LINE);
    } finally {
      setVoiceConfirmBusy(false);
    }
  }, [voiceConfirmBusy, commitPreview, voicePendingPreviews]);

  // opts.silent — verbal-confirm path: the model is MID-TURN awaiting the
  // tool result and will speak from it, so do NOT request a follow-up response
  // (one response per turn — hard rule).
  const executePlan = useCallback(async (p: PendingPreview, opts?: { silent?: boolean }): Promise<PlanStepResult[] | null> => {
    // Drive the card's run state from here so BOTH commit paths (tap and
    // verbal confirm) animate it identically.
    const setPlanRun = (planRun: PendingPreview["planRun"]) =>
      setVoicePendingPreviews((prev) => prev.map((x) => (x.id === p.id ? { ...x, planRun } : x)));
    setPlanRun({ running: true, results: null });
    // Pre-exec note: plans take multiple server round-trips — plenty of time
    // for John to ask "is it done?" mid-run. Upgraded with real counts below.
    const preNote = voiceSessionRef.current?.notifyContext(
      `[The plan "${p.title}" is running RIGHT NOW (user-approved). Treat it as being committed; never say it's waiting on me.]`,
    );
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch("/api/ai/execute-plan", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          business_id: callBusinessIdRef.current,
          acting_user_id: callActingUserIdRef.current,
          goal: p.title,
          steps: p.plan ?? [],
          time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok || data.error) {
        setPlanRun(undefined);
        voiceSessionRef.current?.replaceTapNote(preNote, `[CORRECTION: the plan "${p.title}" FAILED to run (${String(data.error || resp.status)}) — nothing committed. Tell me it failed; do not claim otherwise.]`);
        toast.error(String(data.error || "Couldn't run the plan — try again."));
        return null;
      }
      const results = (data.results ?? []) as PlanStepResult[];
      const total = p.plan?.length ?? results.length;
      const okCount = results.filter((r) => r.ok).length;
      const failed = results.find((r) => !r.ok) || null;
      const summary = okCount === total
        ? `Done ✓ — ${p.title} (${okCount}/${total} steps)`
        : `${p.title}: ${okCount}/${total} steps completed — "${failed?.title ?? "a step"}" failed.`;
      setPlanRun({ running: false, results });
      setVoiceTurns((prev) => [...prev, { role: "assistant", text: summary }]);
      // Honest outcome for the model: name the failed step + WHY. The vague
      // "N/M committed" form let Grok answer "It's running now" right after a
      // failure (2026-07-10) — never again.
      voiceSessionRef.current?.replaceTapNote(
        preNote,
        okCount === total
          ? `[The plan "${p.title}" ran: all ${total} steps committed. Do NOT re-run any of it.]`
          : `[The plan "${p.title}" PARTIALLY ran: steps 1-${okCount} committed, then step ${failed?.index} ("${failed?.title}") FAILED: ${failed?.error} Nothing after it ran. Tell me plainly that step failed and why, and offer to finish it another way. Do NOT say it's running or done, and do NOT re-run the committed steps.]`,
      );
      // One short spoken line after the plan — on failure it must surface the
      // failure, not the generic "confirm it's done" line. Skipped on the
      // verbal path: there the model is already speaking from the tool result.
      if (!opts?.silent) {
        voiceSessionRef.current?.requestFollowUp(
          okCount === total
            ? FOLLOW_UP_LINE
            : "Speak exactly ONE short, natural sentence: say which step failed and why, and offer to finish that part another way. Do not claim anything else committed beyond what the notes say.",
        );
      }
      emitCrmDataChanged();
      return results;
    } catch (e) {
      setPlanRun(undefined);
      voiceSessionRef.current?.replaceTapNote(preNote, `[CORRECTION: the plan "${p.title}" FAILED to run — nothing committed. Tell me it failed; do not claim otherwise.]`);
      toast.error(e instanceof Error ? e.message : "Couldn't run the plan — try again.");
      return null;
    }
  }, []);
  // Registered for the verbal-confirm intercept in execVoiceTool (defined
  // above with a frozen closure).
  useEffect(() => { executePlanRef.current = executePlan; }, [executePlan]);

  const sendCampaign = useCallback(async (p: PendingPreview, drafts: CampaignDraft[]): Promise<CampaignSendResult | null> => {
    // Pre-exec note (see commitPreview): the batch send takes a while — Grok
    // must not claim it's "waiting on confirmation" if asked mid-send.
    const campaignPreNote = voiceSessionRef.current?.notifyContext(
      `[I tapped Send all — the campaign "${p.title}" is sending RIGHT NOW. Treat it as sent; never say it's waiting on me.]`,
    );
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch("/api/ai/tool-exec", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          name: "draft_sms_campaign",
          args: { ...p.args, confirmed: true, drafts },
          business_id: callBusinessIdRef.current,
          acting_user_id: callActingUserIdRef.current,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      const result = (data.result ?? data) as Record<string, unknown>;
      if (!resp.ok || result?.error) {
        voiceSessionRef.current?.replaceTapNote(campaignPreNote, `[CORRECTION: the campaign "${p.title}" FAILED to send (${String(result?.error || resp.status)}) — no texts went out.]`);
        toast.error(String(result?.error || "Couldn't send the batch — try again."));
        return null;
      }
      const sent = (result.sent as CampaignSendResult["sent"]) ?? [];
      const failed = (result.failed as CampaignSendResult["failed"]) ?? [];
      const summary = `Sent ${sent.length} text${sent.length === 1 ? "" : "s"}${failed.length ? ` — ${failed.length} failed` : ""}.`;
      setVoiceTurns((prev) => [...prev, { role: "assistant", text: `${summary} ✓` }]);
      // Silent context — the on-screen summary already confirmed it.
      voiceSessionRef.current?.replaceTapNote(
        campaignPreNote,
        `[I tapped Send all — the campaign went out: ${summary} Do NOT send it again.]`,
      );
      return { sent, failed };
    } catch (e) {
      voiceSessionRef.current?.replaceTapNote(campaignPreNote, `[CORRECTION: the campaign "${p.title}" FAILED to send — no texts went out.]`);
      toast.error(e instanceof Error ? e.message : "Couldn't send the batch — try again.");
      return null;
    }
  }, []);

  // ── Real-time voice persistence ──
  // voiceSavedCountRef tracks how many turns are already on the server; each
  // sync APPENDS only the new ones. The still-growing last turn is held back
  // until the next turn lands (or a final flush includes it).
  const syncVoiceTurns = useCallback((opts?: { includeLast?: boolean; keepalive?: boolean }) => {
    const run = async () => {
      const turns = voiceTurnsRef.current;
      const upto = opts?.includeLast ? turns.length : Math.max(turns.length - 1, 0);
      if (upto <= voiceSavedCountRef.current) return;
      const slice = turns.slice(voiceSavedCountRef.current, upto);
      const asMessages = voiceTurnsToMessages(slice);
      if (asMessages.length === 0) { voiceSavedCountRef.current = upto; return; }
      try {
        // Fresh token per save — a voice call can outlive the ~1h JWT, and a
        // token frozen at call start made every late save 401 "Invalid token"
        // (John's 2026-07-09 session). The cache exists ONLY for keepalive
        // flushes (pagehide/unmount can't await); it's re-primed on every
        // successful fresh fetch here and on a 4-minute interval below.
        let headers: Record<string, string> | null = null;
        if (!opts?.keepalive) {
          const fresh = await getAuthHeaders().catch(() => null);
          if (fresh?.Authorization) { cachedHeadersRef.current = fresh; headers = fresh; }
        }
        if (!headers) headers = cachedHeadersRef.current;
        if (!headers?.Authorization) return; // nothing usable — next turn retries
        const post = (h: Record<string, string>) => fetch("/api/ai/save-voice-thread", {
          method: "POST",
          headers: { ...h, "Content-Type": "application/json" },
          credentials: "include",
          keepalive: opts?.keepalive ?? false,
          body: JSON.stringify({
            business_id: callBusinessIdRef.current,
            thread_id: callThreadIdRef.current ?? undefined,
            messages: asMessages,
            // Tag record-scoped calls so the thread files under the record —
            // untagged voice threads broke per-record history (2026-07-10).
            record_type: callRecordScopeRef.current?.type ?? undefined,
            record_id: callRecordScopeRef.current?.id ?? undefined,
          }),
        });
        let resp = await post(headers);
        if (resp.status === 401 && !opts?.keepalive) {
          // Token expired mid-flight — one retry on a just-refreshed token.
          const fresh = await getAuthHeaders().catch(() => null);
          if (fresh?.Authorization && fresh.Authorization !== headers.Authorization) {
            cachedHeadersRef.current = fresh;
            resp = await post(fresh);
          }
        }
        if (resp.ok) {
          voiceSavedCountRef.current = upto;
          const data = await resp.json().catch(() => ({}));
          if (data.thread?.id) {
            callThreadIdRef.current = data.thread.id;
            setCallThreadId(data.thread.id);
          }
        }
      } catch { /* transient — retried on the next turn / final flush */ }
    };
    syncChainRef.current = syncChainRef.current.then(run, run);
    return syncChainRef.current;
  }, []);

  // Debounced sync as turns land.
  useEffect(() => {
    if (!voiceActive) return;
    if (voiceTurns.length - 1 <= voiceSavedCountRef.current) return;
    const t = window.setTimeout(() => { void syncVoiceTurns(); }, 1200);
    return () => window.clearTimeout(t);
  }, [voiceTurns, voiceActive, syncVoiceTurns]);

  // Tab/app closing mid-call → flush everything with keepalive.
  useEffect(() => {
    const flush = () => { if (voiceSessionRef.current) void syncVoiceTurns({ includeLast: true, keepalive: true }); };
    window.addEventListener("pagehide", flush);
    return () => window.removeEventListener("pagehide", flush);
  }, [syncVoiceTurns]);

  // Keepalive flushes can't await a token refresh, so keep the cached headers
  // young for them: re-prime every 4 minutes while a call is live (Supabase
  // auto-refreshes the JWT, so getSession() hands back a current token).
  useEffect(() => {
    if (!voiceActive) return;
    const t = window.setInterval(() => {
      void getAuthHeaders().then((h) => { if (h.Authorization) cachedHeadersRef.current = h; }).catch(() => {});
    }, 4 * 60 * 1000);
    return () => window.clearInterval(t);
  }, [voiceActive]);

  // Predict the user's next taps after Grok speaks. Best-effort.
  const fetchVoiceSuggestions = useCallback(async (userText: string, assistantText: string) => {
    if (!assistantText) return;
    try {
      // Fresh token each time (same stale-cache 401 as the saves) — and each
      // success re-primes the keepalive cache as a side benefit.
      const headers = await getAuthHeaders();
      if (!headers.Authorization) return;
      cachedHeadersRef.current = headers;
      const resp = await fetch("/api/ai/suggest", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ business_id: callBusinessIdRef.current, user_text: userText, assistant_text: assistantText }),
      });
      if (!resp.ok) return;
      const data = await resp.json().catch(() => ({}));
      const list = Array.isArray(data.suggestions) ? data.suggestions.filter((s: unknown) => typeof s === "string") : [];
      if (voiceSessionRef.current) setVoiceSuggestions(list.slice(0, 4));
    } catch { /* no chips */ }
  }, []);

  const sendVoiceSuggestion = useCallback((text: string) => {
    setVoiceSuggestions([]);
    voiceSessionRef.current?.interrupt();
    voiceSessionRef.current?.sendUserText(text);
  }, []);

  const startVoice = useCallback(async (opts: StartVoiceOpts): Promise<StartVoiceResult> => {
    // Singleton: never silently drop a live call. Same record → the caller just
    // reveals the overlay; different record → the caller shows the nudge.
    if (voiceSessionRef.current || voiceActiveRef.current) {
      return sameRecordScope(callRecordScopeRef.current, opts.recordScope)
        ? { status: "already-active-same" }
        : { status: "already-active-different", activeScope: callRecordScopeRef.current };
    }
    // Capture identity — refs (read by callbacks) + state (for render).
    callBusinessIdRef.current = opts.businessId;
    callActingUserIdRef.current = opts.actingUserId;
    callRecordScopeRef.current = opts.recordScope;
    callThreadIdRef.current = opts.threadId ?? null;
    setCallRecordScope(opts.recordScope);
    setCallThreadId(opts.threadId ?? null);
    setPriorMessages(opts.priorMessages);

    setVoiceError(null);
    setVoiceTurns([]);
    setVoiceSuggestions([]);
    setVoicePendingPreviews([]);
    setVoicePendingAction(null);
    setVoiceUploadTarget(null);
    setFinalizedCall(null); // drop any stale merge-back marker from a prior call
    voiceSavedCountRef.current = 0;
    // Prime the cached auth headers so a pagehide flush never has to await.
    // Guarded: caching a header set WITHOUT Authorization (session not loaded
    // yet) poisoned every keepalive save of the call with a 401.
    cachedHeadersRef.current = null;
    void getAuthHeaders().then((h) => { if (h.Authorization) cachedHeadersRef.current = h; }).catch(() => {});
    lastAssistantTextRef.current = "";
    setVoiceActive(true);
    voiceActiveRef.current = true;
    setVoiceStatus("connecting");
    // Fetch+decode the three MP3 cues while the socket connects so the start
    // cue plays the instant the mic goes live.
    if (voiceSoundsRef.current) preloadVoiceCues();

    // Exactly THREE sounds per call, ever: connect (mic goes live), disconnect
    // (call ends), and the thinking ambience while Grok works. All gated by
    // one pref; nothing else beeps between turns.
    let readyCuePlayed = false;
    let endCuePlayed = false;
    const session = new GrokVoiceSession({
      onStatus: (s) => {
        if (s === "listening" && !readyCuePlayed) { readyCuePlayed = true; if (voiceSoundsRef.current) playVoiceCue("start"); }
        if (s === "ended" && !endCuePlayed) { endCuePlayed = true; if (voiceSoundsRef.current) playVoiceCue("stop"); }
        // A transient realtime error is stale the moment Grok speaks normally.
        if (s === "speaking" && transientErrorRef.current) { transientErrorRef.current = false; setVoiceError(null); }
        setVoiceStatus(s);
      },
      onLevel: (rms) => setVoiceLevel(rms),
      onOutputLevel: (rms) => setVoiceOutLevel(rms),
      onAssistantTurnStart: () => {
        assistantNewTurnRef.current = true;
        setVoiceSuggestions([]);
        // A fresh response supersedes any transient error banner.
        if (transientErrorRef.current) { transientErrorRef.current = false; setVoiceError(null); }
      },
      onUserTranscript: (t, final) => {
        // Speaker-echo guard: drop transcripts that echo Grok's own last words.
        const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
        const heard = norm(t);
        if (heard.length >= 5 && norm(lastAssistantTextRef.current).includes(heard)) return;
        if (final && t) lastUserTextRef.current = t;
        setVoiceSuggestions([]);
        appendVoiceTurn("user", t, !final ? true : true);
      },
      onAssistantTranscript: (t, final) => {
        lastAssistantTextRef.current = final ? t : (lastAssistantTextRef.current + t).slice(-4000);
        appendVoiceTurn("assistant", t, final);
        if (final && t) void fetchVoiceSuggestions(lastUserTextRef.current, t);
      },
      onError: (m, _code, transient) => {
        transientErrorRef.current = !!transient;
        setVoiceError(m);
      },
      onToolCall: execVoiceTool,
      // The echo guard also protects cue windows — the start chime used to
      // echo into the mic and trip server VAD mid-greeting.
      isExternalAudioActive: isVoiceCueActive,
    });
    session.setSpeed(voiceSpeedRef.current);
    session.setVolume(voiceVolumeRef.current / 100);
    session.setEchoGuard(echoGuardRef.current);
    voiceSessionRef.current = session;
    await session.start({
      getAuthHeaders,
      businessId: opts.businessId,
      actingUserId: opts.actingUserId,
      contextText: opts.contextText,
      greet: opts.greet,
      recordType: opts.recordScope?.type ?? null,
      recordId: opts.recordScope?.id ?? null,
    });
    return { status: "started" };
  }, [appendVoiceTurn, execVoiceTool, fetchVoiceSuggestions]);

  // Speed changes apply to the LIVE session immediately and persist per user.
  useEffect(() => { voiceSessionRef.current?.setSpeed(voiceSpeed); }, [voiceSpeed]);
  // So do playback volume and the echo guard (settings sliders mid-call).
  useEffect(() => { voiceSessionRef.current?.setVolume(voiceVolume / 100); }, [voiceVolume]);
  useEffect(() => { voiceSessionRef.current?.setEchoGuard(echoGuard); }, [echoGuard]);

  // Thinking ambience while Grok is actually working — silent the moment he
  // starts speaking or goes back to listening.
  useEffect(() => {
    const working = voiceActive && voiceSoundsOn && (voiceStatus === "thinking" || !!voiceActivity);
    if (working) startThinkingChime();
    else stopThinkingChime();
    return () => stopThinkingChime();
  }, [voiceActive, voiceStatus, voiceActivity, voiceSoundsOn]);

  const stopVoice = useCallback(() => {
    voiceSessionRef.current?.stop();
    voiceSessionRef.current = null;
    setVoiceActivity(null);
    // Publish the finalized transcript so the surface that OWNS this thread can
    // merge it into its visible messages (one conversation across voice+text).
    // Turns are already server-persisted, so any non-owning surface loses nothing.
    const turns = voiceTurnsRef.current;
    const asMessages = voiceTurnsToMessages(turns);
    setFinalizedCall({ threadId: callThreadIdRef.current, recordScope: callRecordScopeRef.current, messages: asMessages });
    if (asMessages.length > 0) {
      // Final flush appends only the remainder (incl. the last turn).
      void syncVoiceTurns({ includeLast: true });
    }
    setVoiceActive(false);
    voiceActiveRef.current = false;
    // Next call must never inherit this call's (possibly expired) token.
    cachedHeadersRef.current = null;
    setVoiceStatus("idle");
    setVoiceMuted(false);
    setVoiceLevel(0);
    setVoiceOutLevel(0);
    setVoiceSuggestions([]);
    setVoicePendingPreviews([]);
    setVoicePendingAction(null);
    setVoiceUploadTarget(null);
    callRecordScopeRef.current = null;
    setCallRecordScope(null);
  }, [syncVoiceTurns]);

  const toggleMute = useCallback(() => {
    setVoiceMuted((m) => {
      const next = !m;
      voiceSessionRef.current?.setMuted(next);
      return next;
    });
  }, []);

  // The banner's dismiss × — the call keeps running underneath.
  const clearVoiceError = useCallback(() => {
    transientErrorRef.current = false;
    setVoiceError(null);
  }, []);

  // Leaving the dashboard entirely (provider unmount) → stop + flush. This is
  // the ONLY place a surface teardown ends the call now; closing a detail sheet
  // no longer reaches it, so the call roams. (App/tab close is handled by the
  // pagehide flush above.)
  useEffect(() => () => {
    if (voiceSessionRef.current) {
      voiceSessionRef.current.stop();
      void syncVoiceTurns({ includeLast: true, keepalive: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo<CopilotVoiceValue>(() => ({
    voiceActive, voiceStatus, voiceLevel, voiceOutLevel, voiceMuted, voiceError, voiceTurns, voiceSuggestions, voiceActivity,
    voiceSpeed, setVoiceSpeed,
    callRecordScope, callThreadId, priorMessages,
    voicePendingPreviews, setVoicePendingPreviews, voicePendingAction, setVoicePendingAction,
    voiceUploadTarget, setVoiceUploadTarget, voiceConfirmBusy, voiceCrmSendBusy,
    startVoice, stopVoice, toggleMute, sendVoiceSuggestion, clearVoiceError,
    confirmPending, confirmAllPending, executePlan, sendCampaign, sendSmsViaCrmNow,
    finalizedCall, clearFinalizedCall,
  }), [
    voiceActive, voiceStatus, voiceLevel, voiceOutLevel, voiceMuted, voiceError, voiceTurns, voiceSuggestions, voiceActivity,
    voiceSpeed, setVoiceSpeed,
    callRecordScope, callThreadId, priorMessages,
    voicePendingPreviews, voicePendingAction, voiceUploadTarget, voiceConfirmBusy, voiceCrmSendBusy,
    startVoice, stopVoice, toggleMute, sendVoiceSuggestion, clearVoiceError,
    confirmPending, confirmAllPending, executePlan, sendCampaign, sendSmsViaCrmNow,
    finalizedCall, clearFinalizedCall,
  ]);

  return <CopilotVoiceContext.Provider value={value}>{children}</CopilotVoiceContext.Provider>;
}

export function useCopilotVoice(): CopilotVoiceValue {
  const ctx = useContext(CopilotVoiceContext);
  if (!ctx) throw new Error("useCopilotVoice must be used inside CopilotVoiceProvider");
  return ctx;
}
